import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Navigation } from "lucide-react";
import PageHero from "../components/PageHero";
import Contact from "../components/Contact";
import { SectionHeading } from "../components/SectionHeading";
import { site, institutions } from "../mock/mock";
import { logContactEvent } from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function ContactPage() {
  useSeo({
    title: "Contact & Location",
    path: "/contact",
    description:
      "Contact The KPR Academy, KPR Campus, Arasur, Coimbatore 641407. Admission hotline +91 97906 15551 and +91 97917 15551, WhatsApp enquiry, email and directions to campus.",
    keywords:
      "KPR Academy contact, school Arasur Coimbatore address, admission hotline Coimbatore, KPR Academy phone number",
  });
  const cards = [
    {
      icon: Phone,
      title: "Admission hotline",
      lines: site.hotlines,
      hrefs: site.hotlines.map((h) => `tel:${h.replace(/\s/g, "")}`),
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      lines: site.whatsapps.map((w) => w.display),
      hrefs: site.whatsapps.map((w) => `https://wa.me/${w.number}`),
    },
    { icon: Mail, title: "Email", lines: [site.email], hrefs: [`mailto:${site.email}`] },
    { icon: Clock, title: "Office hours", lines: ["Mon – Sat", "9:00 AM to 4:00 PM"], hrefs: [] },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Come and see the campus"
        sub="Call the hotline, WhatsApp us, or drop by the Arasur campus — our team will walk you through everything."
        crumbs={[{ label: "Contact" }]}
        image="/campus/school-4.jpg"
      />

      <section className="py-24 lg:py-28" style={{ background: "var(--cream)" }}>
        <div className="mx-auto max-w-[1280px] px-5 lg:px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c, i) => (
              <div
                key={c.title}
                data-reveal=""
                style={{ transitionDelay: `${i * 80}ms`, border: "1px solid rgba(11,59,54,.09)" }}
                className="rounded-2xl bg-white p-7"
              >
                <span
                  className="grid h-12 w-12 place-items-center rounded-xl"
                  style={{ background: "var(--teal-100)", color: "var(--teal-700)" }}
                >
                  <c.icon size={19} strokeWidth={1.7} />
                </span>
                <p className="mt-6 text-[0.7rem] uppercase tracking-[0.16em]" style={{ color: "var(--gold)" }}>
                  {c.title}
                </p>
                {c.lines.map((l, k) =>
                  c.hrefs[k] ? (
                    <a
                      key={l}
                      href={c.hrefs[k]}
                      target={c.hrefs[k].startsWith("http") ? "_blank" : undefined}
                      rel="noreferrer"
                      onClick={() => {
                        if (c.hrefs[k].startsWith("https://wa.me/")) {
                          logContactEvent({
                            channel: "whatsapp",
                            number: c.hrefs[k].split("/").pop(),
                            display: l,
                            label: "contact page card",
                          });
                        } else if (c.hrefs[k].startsWith("tel:")) {
                          logContactEvent({
                            channel: "call",
                            number: c.hrefs[k].replace("tel:", ""),
                            display: l,
                            label: "contact page card",
                          });
                        }
                      }}
                      className="font-display mt-2 block break-all text-[0.98rem]"
                      style={{ color: "var(--teal-900)" }}
                    >
                      {l}
                    </a>
                  ) : (
                    <p key={l} className="font-display mt-2 text-[1.05rem]" style={{ color: "var(--teal-900)" }}>
                      {l}
                    </p>
                  )
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
            <div data-reveal="">
              <SectionHeading eyebrow="Reach us" title="The KPR Academy, Arasur" />
              <p className="mt-6 flex gap-3 text-[0.98rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                <MapPin size={18} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 3 }} />
                {site.address}
              </p>
              <a
                href={site.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-3 rounded-full px-7 py-3.5 text-[0.86rem] font-semibold"
                style={{ background: "var(--teal-800)", color: "#FBF7F0" }}
              >
                <Navigation size={16} /> Get directions
              </a>

              <div className="mt-10">
                <p className="eyebrow" style={{ color: "var(--gold)" }}>
                  Other KPR institutions
                </p>
                <div className="mt-4 space-y-2">
                  {institutions.map((i) => (
                    <a
                      key={i.short}
                      href={i.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block text-[0.9rem]"
                      style={{ color: "var(--teal-800)" }}
                    >
                      {i.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="overflow-hidden rounded-2xl"
              data-reveal=""
              style={{ border: "1px solid rgba(11,59,54,.1)", minHeight: 380 }}
            >
              <iframe
                title="KPR Academy location"
                src="https://www.google.com/maps?q=KPR%20Institute%20of%20Engineering%20and%20Technology%20Arasur%20Coimbatore&output=embed"
                width="100%"
                height="420"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </>
  );
}
